import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import SignupFormModal from '../components/SignupFormModal';

export default function PortfolioPage() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [activeFilter, setActiveFilter] = useState('All');
    const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
    const [modalButtonType, setModalButtonType] = useState('demo');
    const [selectedVideo, setSelectedVideo] = useState(null);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const filters = [
        'All',
        'Whatsapp Automation',
        'Email Automation',
        'AI & Lead Qualification',
        'Scheduling & Reminder',
        'CRM Integration'
    ];

    const automations = [
        {
            title: "Instant Lead Response Bot",
            category: "Whatsapp Automation",
            tags: "WHATSAPP + EMAIL AUTOMATION",
            description: "Replies to new leads within 30 seconds across WhatsApp, Email, and DMs — ensuring no inquiry is missed.",
            tools: ["360Dialog", "Mailersend", "Make.com"],
            image: "https://images.unsplash.com/photo-1531482615713-2afd69097998?w=400&h=300&fit=crop",
            bgColor: "from-purple-500 to-indigo-600",
            videoUrl: "https://www.youtube.com/embed/fRVY2-Tailc?autoplay=1"
        },
        {
            title: "Lead Qualification Flow",
            category: "AI & Lead Qualification",
            tags: "AI LEAD SCORING + CRM SYNC",
            description: "Automatically asks prospects key questions like \"What's your budget?\" and pushes only hot leads to sales reps.",
            tools: ["OpenAI API", "Airtable", "Make.com"],
            image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop",
            bgColor: "from-white to-gray-100",
            videoUrl: "https://www.youtube.com/embed/fRVY2-Tailc?autoplay=1"
        },
        {
            title: "Instant Booking System",
            category: "Scheduling & Reminder",
            tags: "BOOK AUTOMATION",
            description: "Prospects can instantly book test drives, property viewings, or consultations without back-and-forth messaging.",
            tools: ["Calendly", "Mailersend", "Make.com"],
            image: "https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=400&h=300&fit=crop",
            bgColor: "from-orange-200 to-orange-100",
            videoUrl: "https://www.youtube.com/embed/fRVY2-Tailc?autoplay=1"
        },
        {
            title: "Reminder Sequence",
            category: "Scheduling & Reminder",
            tags: "FOLLOW-UP & ATTENDANCE AUTOMATION",
            description: "Automated reminder sequences to ensure prospects never miss appointments or follow-ups.",
            tools: ["Twilio", "Mailersend", "Make.com"],
            image: "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&h=300&fit=crop",
            bgColor: "from-white to-gray-100",
            videoUrl: "https://www.youtube.com/embed/fRVY2-Tailc?autoplay=1"
        },
        {
            title: "CRM Sync Automation",
            category: "CRM Integration",
            tags: "AIRTABLE / HUBSPOT SYNC",
            description: "Seamlessly sync all leads and customer data across your CRM platforms in real-time.",
            tools: ["HubSpot", "Airtable", "Make.com"],
            image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop",
            bgColor: "from-slate-700 to-slate-900",
            videoUrl: "https://www.youtube.com/embed/fRVY2-Tailc?autoplay=1"
        },
        {
            title: "Always-On Lead Engagement",
            category: "Email Automation",
            tags: "INBOUND AUTOMATION",
            description: "Initiates real-time responses to incoming inquiries across WhatsApp, Email, and DMs — maintaining momentum from first contact.",
            tools: ["Mailchimp", "SendGrid", "Make.com"],
            image: "https://images.unsplash.com/photo-1596526131083-e8c633c948d2?w=400&h=300&fit=crop",
            bgColor: "from-orange-200 to-pink-200",
            videoUrl: "https://www.youtube.com/embed/fRVY2-Tailc?autoplay=1"
        },
        {
            title: "Smart Email Sequences",
            category: "Email Automation",
            tags: "DRIP CAMPAIGNS",
            description: "Automated email sequences that nurture leads and convert them into paying customers.",
            tools: ["Mailchimp", "ActiveCampaign", "Zapier"],
            image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=400&h=300&fit=crop",
            bgColor: "from-blue-400 to-blue-600",
            videoUrl: "https://www.youtube.com/embed/fRVY2-Tailc?autoplay=1"
        },
        {
            title: "WhatsApp Business Flow",
            category: "Whatsapp Automation",
            tags: "CONVERSATIONAL AI",
            description: "Intelligent WhatsApp chatbots that qualify leads, answer FAQs, and book appointments automatically.",
            tools: ["360Dialog", "Twilio", "Make.com"],
            image: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=400&h=300&fit=crop",
            bgColor: "from-green-400 to-green-600",
            videoUrl: "https://www.youtube.com/embed/fRVY2-Tailc?autoplay=1"
        },
        {
            title: "Multi-Platform CRM Bridge",
            category: "CRM Integration",
            tags: "SALESFORCE + HUBSPOT",
            description: "Connect multiple CRM systems and ensure data consistency across all platforms.",
            tools: ["Salesforce", "HubSpot", "Zapier"],
            image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop",
            bgColor: "from-indigo-500 to-purple-600",
            videoUrl: "https://www.youtube.com/embed/fRVY2-Tailc?autoplay=1"
        }
    ];

    const filteredAutomations = activeFilter === 'All'
        ? automations
        : automations.filter(automation => automation.category === activeFilter);

    // Video Modal Component
    const VideoModal = ({ videoUrl, onClose }) => {
        if (!videoUrl) return null;

        return (
            <div
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90"
                onClick={onClose}
            >
                <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
                    <button
                        onClick={onClose}
                        className="absolute -top-12 right-0 text-white hover:text-orange-500 transition-colors"
                    >
                        <X size={32} />
                    </button>
                    <div className="relative pt-[56.25%] bg-black rounded-lg overflow-hidden shadow-2xl">
                        <iframe
                            className="absolute inset-0 w-full h-full"
                            src={videoUrl}
                            title="Demo Video"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-white font-['Outfit',sans-serif] text-gray-900">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
        
        .text-gradient {
          background: linear-gradient(135deg, #FF6B35 0%, #FFA500 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .filter-tab {
          transition: all 0.3s ease;
        }

        .filter-tab.active {
          background: linear-gradient(135deg, #FF6B35 0%, #FFA500 100%);
          color: white;
        }

        .automation-card {
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .automation-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }

        .play-button {
          transition: all 0.3s ease;
        }

        .automation-card:hover .play-button {
          transform: scale(1.1);
        }
      `}</style>

            {/* Navigation - keeping existing nav code */}
            <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white'}`}>
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        <Link to="/" className="flex items-center gap-2">
                            <img src="/logo-qorvix.png" alt="Qorvix Logo" className="h-10 w-auto" />
                        </Link>
                        <div className="hidden lg:flex items-center space-x-8">
                            <Link to="/" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Home</Link>
                            <Link to="/about" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">About</Link>
                            <Link to="/how-it-works" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">How it works</Link>
                            <Link to="/portfolio" className="text-orange-500 transition-colors font-medium text-[15px]">Portfolio</Link>
                            <div className="relative group">
                                <button className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px] flex items-center gap-1">
                                    Resources
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                                    <div className="py-2">
                                        <a href="/documentation" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Documentation</a>
                                        <a href="/integrations" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Integration</a>
                                        <Link to="/faq" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">FAQs</Link>
                                        <a href="/help-center" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Help center</a>
                                        <a href="/roadmap" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Roadmap</a>
                                    </div>
                                </div>
                            </div>
                            <Link to="/contact" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Contact us</Link>
                            <button onClick={() => {
                                setModalButtonType('demo');
                                setIsSignupModalOpen(true);
                            }} className="bg-transparent border-2 border-orange-500 text-orange-500 px-6 py-2.5 rounded-lg hover:bg-orange-500 hover:text-white transition-all font-medium text-[15px]">
                                Automate Now
                            </button>
                        </div>
                        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden text-gray-900">
                            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)}></div>
                    <div className="fixed top-8 left-1/2 -translate-x-1/2 w-[90%] max-w-sm bg-white rounded-3xl shadow-2xl p-8 z-50">
                        <button onClick={() => setMobileMenuOpen(false)} className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
                            <X size={24} />
                        </button>
                        <nav className="mt-4 space-y-6">
                            <Link to="/" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Home</Link>
                            <Link to="/about" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>About</Link>
                            <Link to="/how-it-works" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>How it works</Link>
                            <Link to="/portfolio" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Portfolio</Link>
                            <Link to="/contact" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Contact us</Link>
                            <button onClick={() => { setModalButtonType('demo'); setIsSignupModalOpen(true); }} className="text-lg font-medium text-orange-500 border-b-2 border-orange-500 pb-1">Start free demo</button>
                        </nav>
                    </div>
                </div>
            )}

            {/* Hero Section */}
            <section className="pt-32 pb-16 px-4">
                <div className="max-w-7xl mx-auto text-center">
                    <h1 className="text-5xl md:text-6xl font-bold mb-6">
                        Our <span className="text-gradient relative">
                            Automation
                            <svg className="absolute -bottom-2 left-0 w-full" height="8" viewBox="0 0 400 8" fill="none">
                                <path d="M0 4 Q 200 8, 400 4" stroke="#FF8C42" strokeWidth="4" fill="none" />
                            </svg>
                        </span>
                    </h1>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-12">
                        We build smart automation systems that convert leads faster, save time, and help teams close more deals with less effort.
                    </p>

                    {/* Filter Tabs */}
                    <div className="flex flex-wrap justify-center gap-3 mb-16">
                        {filters.map((filter) => (
                            <button
                                key={filter}
                                onClick={() => setActiveFilter(filter)}
                                className={`filter-tab px-6 py-2.5 rounded-lg font-medium text-sm ${activeFilter === filter ? 'active' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                            >
                                {filter}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Automation Cards Grid */}
            <section className="pb-20 px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {filteredAutomations.map((automation, index) => (
                            <div
                                key={index}
                                onClick={() => setSelectedVideo(automation.videoUrl)}
                                className="automation-card bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 group"
                            >
                                <div className="p-6">
                                    <h3 className="text-xl font-bold mb-2">{automation.title}</h3>
                                    <p className="text-xs font-semibold text-orange-600 mb-4 uppercase tracking-wide">{automation.tags}</p>
                                    <p className="text-gray-600 text-sm leading-relaxed mb-4">{automation.description}</p>
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {automation.tools.map((tool, toolIndex) => (
                                            <span key={toolIndex} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">{tool}</span>
                                        ))}
                                    </div>

                                    {/* Watch Demo Button */}
                                    <div className="flex items-center gap-2 text-orange-500 font-semibold text-sm mt-4">
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                                        </svg>
                                        Watch Demo
                                    </div>
                                </div>
                                <div className={`h-48 bg-gradient-to-br ${automation.bgColor} flex items-center justify-center overflow-hidden relative`}>
                                    <img src={automation.image} alt={automation.title} className="w-full h-full object-cover opacity-80" />

                                    {/* Play Button Overlay */}
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-all">
                                        <div className="play-button w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                                            <svg className="w-8 h-8 text-orange-500 ml-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* No Results Message */}
                    {filteredAutomations.length === 0 && (
                        <div className="text-center py-12">
                            <p className="text-gray-500 text-lg">No automations found for this category.</p>
                        </div>
                    )}

                    {/* Start a Project Button */}
                    <div className="text-center mt-16">
                        <button onClick={() => { setModalButtonType('demo'); setIsSignupModalOpen(true); }} className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-10 py-4 rounded-lg text-lg font-semibold hover:shadow-xl transition-all">
                            Start a project
                        </button>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-400 via-orange-300 to-purple-400"></div>
                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-800">
                        Access Your Business Automation Potential Today & Unlock Smarter Growth
                    </h2>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                        <button onClick={() => { setModalButtonType('demo'); setIsSignupModalOpen(true); }} className="bg-orange-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-700 transition-all">
                            Talk To Automation Expert
                        </button>
                        <button className="border-2 border-white text-white bg-transparent px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-orange-600 transition-all">
                            See Prices & Plans
                        </button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-white py-12 px-4 border-t border-gray-200">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <h3 className="text-xl font-bold mb-4"><span className="text-gradient">Qorvix</span> — Automate What Matters</h3>
                            <p className="text-gray-600 text-sm leading-relaxed mb-6">Qorvix helps businesses automate workflows, connect tools, and scale faster using AI-powered systems.</p>
                        </div>
                        <div>
                            <h4 className="font-bold mb-4">Contact</h4>
                            <div className="space-y-2 text-sm text-gray-600">
                                <p>London, England, UK</p>
                                <p>+44 7757 233260</p>
                                <p>Support@qorvix.ai</p>
                            </div>
                        </div>
                        <div>
                            <h4 className="font-bold mb-4">Company</h4>
                            <div className="space-y-2 text-sm">
                                <Link to="/" className="block text-gray-600 hover:text-gray-900 transition-colors">Home</Link>
                                <Link to="/about" className="block text-gray-600 hover:text-gray-900 transition-colors">About</Link>
                                <Link to="/portfolio" className="block text-gray-600 hover:text-gray-900 transition-colors">Portfolio</Link>
                                <Link to="/contact" className="block text-gray-600 hover:text-gray-900 transition-colors">Contact</Link>
                            </div>
                        </div>
                        <div>
                            <h4 className="font-bold mb-4">Resources</h4>
                            <div className="space-y-2 text-sm">
                                <Link to="/documentation" className="block text-gray-600 hover:text-gray-900 transition-colors">Documentation</Link>
                                <Link to="/integrations" className="block text-gray-600 hover:text-gray-900 transition-colors">Integration</Link>
                                <Link to="/faq" className="block text-gray-600 hover:text-gray-900 transition-colors">FAQs</Link>
                            </div>
                        </div>
                    </div>
                    <div className="pt-8 flex items-center justify-between border-t border-gray-200">
                        <div className="text-2xl font-bold text-gradient">Qorvix</div>
                        <div className="text-sm text-gray-600">© Qorvix Technologies. All Rights Reserved.</div>
                    </div>
                </div>
            </footer>

            {/* Video Modal */}
            <VideoModal videoUrl={selectedVideo} onClose={() => setSelectedVideo(null)} />

            <SignupFormModal isOpen={isSignupModalOpen} onClose={() => setIsSignupModalOpen(false)} buttonType={modalButtonType} />
        </div>
    );
}